import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const buildHierarchy = (pages) => {
  const childrenByParent = {};
  const topPages = [];

  pages.forEach((page) => {
    if (page.parentId) {
      if (!childrenByParent[page.parentId]) {
        childrenByParent[page.parentId] = [];
      }
      childrenByParent[page.parentId].push(page);
    } else {
      topPages.push(page);
    }
  });

  return { topPages, childrenByParent };
};

const DragHandle = ({ attributes, listeners }) => (
  <button
    type="button"
    className="wiki-sidebar-drag-handle"
    aria-label="Drag to reorder"
    {...attributes}
    {...listeners}
  >
    <i className="bi bi-grip-vertical" aria-hidden="true"></i>
  </button>
);

const TopLevelItem = ({ page, isActive, hasChildren, isExpanded, onToggle, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: page.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <li ref={setNodeRef} style={style}>
      <div className="wiki-sidebar-item">
        <DragHandle attributes={attributes} listeners={listeners} />
        <button
          type="button"
          className="wiki-sidebar-toggle"
          onClick={() => hasChildren && onToggle(page.id)}
          aria-expanded={hasChildren ? isExpanded : undefined}
          aria-hidden={!hasChildren}
          tabIndex={hasChildren ? 0 : -1}
          style={hasChildren ? undefined : { visibility: 'hidden' }}
        >
          <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'}`} aria-hidden="true"></i>
        </button>
        <Link to={`/wiki/${page.id}`} className={`wiki-sidebar-link${isActive ? ' active' : ''}`}>
          <i className="bi bi-file-earmark-text me-2" aria-hidden="true"></i>
          {page.title}
        </Link>
      </div>
      {children}
    </li>
  );
};

const ChildItem = ({ page, isActive }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: page.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <li ref={setNodeRef} style={style}>
      <div className="wiki-sidebar-item">
        <DragHandle attributes={attributes} listeners={listeners} />
        <Link to={`/wiki/${page.id}`} className={`wiki-sidebar-link${isActive ? ' active' : ''}`}>
          <i className="bi bi-file-earmark-text me-2" aria-hidden="true"></i>
          {page.title}
        </Link>
      </div>
    </li>
  );
};

const ChildList = ({ parentId, children, activePageId, onReorder, sensors }) => {
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = children.findIndex((p) => p.id === active.id);
    const newIndex = children.findIndex((p) => p.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    onReorder(parentId, arrayMove(children, oldIndex, newIndex).map((p) => p.id));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={children.map((p) => p.id)} strategy={verticalListSortingStrategy}>
        <ul className="wiki-sidebar-sublist">
          {children.map((child) => (
            <ChildItem key={child.id} page={child} isActive={child.id === activePageId} />
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
};

const WikiSidebar = ({ pages, loading, error, onReorder }) => {
  const { pageId } = useParams();
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const { topPages, childrenByParent } = useMemo(() => buildHierarchy(pages), [pages]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  // Auto-expand the parent of the currently active child page.
  useEffect(() => {
    if (!pageId) return;
    const activePage = pages.find((page) => page.id === pageId);
    if (!activePage || !activePage.parentId) return;
    setExpandedIds((prev) => {
      if (prev.has(activePage.parentId)) return prev;
      const next = new Set(prev);
      next.add(activePage.parentId);
      return next;
    });
  }, [pageId, pages]);

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleTopLevelDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = topPages.findIndex((p) => p.id === active.id);
    const newIndex = topPages.findIndex((p) => p.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    onReorder(null, arrayMove(topPages, oldIndex, newIndex).map((p) => p.id));
  };

  return (
    <nav className="wiki-sidebar">
      <div className="wiki-sidebar-header">
        <span className="text-muted-gh">Pages</span>
        <Link to="/wiki/new" className="wiki-sidebar-add-btn" title="New page">
          <i className="bi bi-plus-lg" aria-hidden="true"></i>
        </Link>
      </div>

      {error && (
        <div className="wiki-sidebar-message text-danger">{error}</div>
      )}

      {loading ? (
        <div className="wiki-sidebar-message text-muted-gh">
          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          Loading...
        </div>
      ) : topPages.length === 0 ? (
        <div className="wiki-sidebar-message text-muted-gh">No pages yet</div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleTopLevelDragEnd}>
          <SortableContext items={topPages.map((p) => p.id)} strategy={verticalListSortingStrategy}>
            <ul className="wiki-sidebar-list">
              {topPages.map((page) => {
                const children = childrenByParent[page.id] || [];
                const hasChildren = children.length > 0;
                const isExpanded = expandedIds.has(page.id);

                return (
                  <TopLevelItem
                    key={page.id}
                    page={page}
                    isActive={page.id === pageId}
                    hasChildren={hasChildren}
                    isExpanded={isExpanded}
                    onToggle={toggleExpanded}
                  >
                    {hasChildren && isExpanded && (
                      <ChildList
                        parentId={page.id}
                        children={children}
                        activePageId={pageId}
                        onReorder={onReorder}
                        sensors={sensors}
                      />
                    )}
                  </TopLevelItem>
                );
              })}
            </ul>
          </SortableContext>
        </DndContext>
      )}
    </nav>
  );
};

export default WikiSidebar;
